// Roles & Permissions (dynamic-RBAC Phase 2). Two tabs:
//   • Platform roles — a table of role definitions; create custom roles, edit/delete
//     custom ones. System roles are read-only (lock icon, no edit/delete).
//   • Permission catalog — read-only viewer of the code-derived permission catalog.
// Permissions are code; roles are data — see docs/product/dynamic-rbac-role-management.md §7.1.
import React, { useEffect, useState } from 'react';
import {
  Tabs, Table, Button, Tag, Space, Typography, Popconfirm, message, Tooltip,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, LockOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import {
  getCatalog, listRoles, deleteRole, rbacErrorMessage,
  type PermissionCatalog, type PlatformRole,
} from './rbacService';
import PermissionCatalogView from './PermissionCatalogView';
import RoleBuilderDrawer from './RoleBuilderDrawer';

const scopeColor: Record<string, string> = {
  self: 'default',
  team: 'blue',
  tenant: 'geekblue',
  platform: 'gold',
};

const RolesPermissionsPage: React.FC = () => {
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [roles, setRoles] = useState<PlatformRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformRole | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([getCatalog(), listRoles()]);
      setCatalog(c);
      setRoles(r || []);
    } catch (e) {
      message.error(rbacErrorMessage(e, 'Could not load roles & permissions.'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (role: PlatformRole) => {
    setEditing(role);
    setDrawerOpen(true);
  };

  const remove = async (role: PlatformRole) => {
    try {
      await deleteRole(role.id);
      message.success(`Role “${role.label}” deleted.`);
      load();
    } catch (e) {
      message.error(rbacErrorMessage(e, 'Could not delete the role.'));
    }
  };

  const columns = [
    {
      title: 'Role',
      dataIndex: 'label',
      key: 'label',
      render: (label: string, r: PlatformRole) => (
        <Space>
          <Typography.Text strong>{label}</Typography.Text>
          {r.isSystem && (
            <Tooltip title="System role — read-only">
              <LockOutlined style={{ color: 'rgba(128,128,128,0.7)' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (k: string) => <Typography.Text code>{k}</Typography.Text>,
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      render: (s: string) => <Tag color={scopeColor[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (perms: string[]) => <Tag>{perms?.length || 0}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'isSystem',
      key: 'isSystem',
      render: (isSystem: boolean) =>
        isSystem ? <Tag color="purple">System</Tag> : <Tag color="green">Custom</Tag>,
    },
    {
      title: '',
      key: 'action',
      align: 'right' as const,
      render: (_: any, r: PlatformRole) => {
        if (r.isSystem) {
          return <Typography.Text type="secondary">—</Typography.Text>;
        }
        return (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
              Edit
            </Button>
            <Popconfirm
              title={`Delete role “${r.label}”?`}
              description="This cannot be undone. Assigned roles cannot be deleted."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove(r)}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const rolesTab = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Typography.Text type="secondary">
          Roles are named bundles of permissions. Create custom platform roles here; system roles
          are seeded from code and read-only.
        </Typography.Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
            New role
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={roles}
        pagination={false}
        size="middle"
      />
    </Space>
  );

  return (
    <div>
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        Roles &amp; Permissions
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Compose platform roles from the code-enforced permission catalog. Permissions are code;
        roles are data — changes take effect across the platform without a redeploy.
      </Typography.Paragraph>

      <Tabs
        defaultActiveKey="roles"
        items={[
          { key: 'roles', label: 'Platform roles', children: rolesTab },
          {
            key: 'catalog',
            label: 'Permission catalog',
            children: <PermissionCatalogView catalog={catalog} />,
          },
        ]}
      />

      <RoleBuilderDrawer
        open={drawerOpen}
        role={editing}
        catalog={catalog}
        onClose={() => setDrawerOpen(false)}
        onSaved={load}
      />
    </div>
  );
};

export default RolesPermissionsPage;
